import type { NotificationDto } from "@fp/admin-api-client";
import { DestructiveConfirmationModal } from "@fp/ui/components/application/modals/destructive-confirmation-modal";
import { Tabs } from "@fp/ui/components/application/tabs/tabs";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { Button } from "@fp/ui/components/base/buttons/button";
import { CheckboxBase } from "@fp/ui/components/base/checkbox/checkbox";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Inbox01, Loading02 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { NotificationGridRow } from "@/components/notifications/notification-grid-row";
import { getNotificationRoute } from "@/components/notifications/notification-routing";
import {
  useNotificationCounts,
  useNotificationDeleteState,
  useNotificationFeed,
  useNotificationReadState,
} from "@/components/notifications/use-notifications";

const UNREAD_TAB = "unread";
const ALL_TAB = "all";

type NotificationTab = typeof UNREAD_TAB | typeof ALL_TAB;

export const Route = createFileRoute("/notifications")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

function TabLabel({
  label,
  count,
  isSelected,
}: {
  label: string;
  count: number;
  isSelected: boolean;
}) {
  return (
    <>
      {label}
      <Badge size="sm" type="pill-color" color={isSelected ? "brand" : "gray"}>
        {count}
      </Badge>
    </>
  );
}

function RouteComponent() {
  const router = useRouter();
  const [selectedTab, setSelectedTab] = useState<NotificationTab>(UNREAD_TAB);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isEveryMatchSelected, setIsEveryMatchSelected] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<NotificationDto | "selection" | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  const unreadOnly = selectedTab === UNREAD_TAB;
  const { unreadCount, totalCount } = useNotificationCounts();
  const { notifications, isLoading, isError, hasMore, isLoadingMore, loadMore } =
    useNotificationFeed(unreadOnly);
  const readState = useNotificationReadState();
  const deleteState = useNotificationDeleteState();

  const now = useMemo(() => new Date(), [notifications]);

  useEffect(() => {
    const target = sentinel.current;

    if (!target || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMore();
      }
    });

    observer.observe(target);

    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const selectedNotifications = notifications.filter((notification) =>
    selectedIds.has(notification.id),
  );
  const isEveryRowSelected =
    notifications.length > 0 &&
    selectedNotifications.length === notifications.length;
  const matchCount = unreadOnly ? unreadCount : totalCount;
  const tabs = [
    {
      id: UNREAD_TAB,
      label: (
        <TabLabel label="Unread" count={unreadCount} isSelected={unreadOnly} />
      ),
    },
    {
      id: ALL_TAB,
      label: (
        <TabLabel label="All" count={totalCount} isSelected={!unreadOnly} />
      ),
    },
  ];
  const canSelectEveryMatch =
    isEveryRowSelected && !isEveryMatchSelected && matchCount > notifications.length;

  const clearSelection = () => {
    setSelectedIds(new Set());
    setIsEveryMatchSelected(false);
  };

  const changeTab = (tab: NotificationTab) => {
    setSelectedTab(tab);
    clearSelection();
  };

  const toggleSelected = (notificationId: string) => {
    setIsEveryMatchSelected(false);
    setSelectedIds((previous) => {
      const next = new Set(previous);

      if (next.has(notificationId)) {
        next.delete(notificationId);
      } else {
        next.add(notificationId);
      }

      return next;
    });
  };

  const toggleSelectAll = () => {
    if (isEveryRowSelected) {
      clearSelection();
      return;
    }

    setSelectedIds(new Set(notifications.map((notification) => notification.id)));
  };

  const markSelectedRead = () => {
    if (isEveryMatchSelected) {
      readState.markAllRead();
    } else {
      readState.markManyRead(
        selectedNotifications.map((notification) => notification.id),
      );
    }

    clearSelection();
  };

  const markSelectedUnread = () => {
    if (isEveryMatchSelected) {
      readState.markAllUnread();
    } else {
      readState.markManyUnread(
        selectedNotifications.map((notification) => notification.id),
      );
    }

    clearSelection();
  };

  const deleteCount =
    pendingDelete === "selection"
      ? isEveryMatchSelected
        ? matchCount
        : selectedNotifications.length
      : 1;

  const confirmDelete = () => {
    if (!pendingDelete) {
      return;
    }

    const onDeleted = () => {
      clearSelection();
      setPendingDelete(null);
    };

    if (pendingDelete !== "selection") {
      deleteState.deleteMany([pendingDelete.id], onDeleted);
    } else if (isEveryMatchSelected) {
      deleteState.deleteAll(unreadOnly, onDeleted);
    } else {
      deleteState.deleteMany(
        selectedNotifications.map((notification) => notification.id),
        onDeleted,
      );
    }
  };

  const openNotification = (notification: NotificationDto) => {
    if (!notification.isRead) {
      readState.markRead(notification.id);
    }

    const route = getNotificationRoute(notification.typeKey, notification.subjectId);

    if (route) {
      router.history.push(route);
    }
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Notifications"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer className="p-0">
        <div className="flex flex-col bg-primary">
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) =>
              changeTab(key === ALL_TAB ? ALL_TAB : UNREAD_TAB)
            }
          >
            <Tabs.List
              type="underline"
              fullWidth
              items={tabs}
              className="rounded-none border-0 px-4 pt-2"
            >
              {(tab) => <Tabs.Item {...tab} />}
            </Tabs.List>
          </Tabs>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-secondary px-4 py-2">
            <button
              type="button"
              role="checkbox"
              aria-checked={isEveryRowSelected}
              aria-label="Select all loaded notifications"
              onClick={toggleSelectAll}
              disabled={notifications.length === 0}
              className="shrink-0 rounded outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <CheckboxBase
                isSelected={isEveryRowSelected}
                isIndeterminate={
                  selectedNotifications.length > 0 && !isEveryRowSelected
                }
                isDisabled={notifications.length === 0}
              />
            </button>

            {selectedNotifications.length > 0 && (
              <>
                <span className="text-sm whitespace-nowrap text-tertiary">
                  {isEveryMatchSelected
                    ? `All ${matchCount} selected`
                    : `${selectedNotifications.length} selected`}
                </span>
                <Button
                  color="secondary"
                  size="sm"
                  isDisabled={readState.isMarkingMany}
                  onClick={markSelectedRead}
                >
                  Mark read
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  isDisabled={readState.isMarkingMany}
                  onClick={markSelectedUnread}
                >
                  Mark unread
                </Button>
                <Button
                  color="secondary-destructive"
                  size="sm"
                  isDisabled={deleteState.isDeleting}
                  onClick={() => setPendingDelete("selection")}
                >
                  Delete
                </Button>
                {canSelectEveryMatch && (
                  <Button
                    color="link-color"
                    size="sm"
                    onClick={() => setIsEveryMatchSelected(true)}
                  >
                    Select all {matchCount}
                  </Button>
                )}
              </>
            )}
          </div>

          {isLoading && (
            <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-tertiary">
              <Loading02 className="size-4 animate-spin" />
              Loading notifications…
            </div>
          )}

          {isError && (
            <div className="px-4 py-12 text-center text-sm text-error-primary">
              Failed to load notifications.
            </div>
          )}

          {!isLoading && !isError && notifications.length === 0 && (
            <div className="flex flex-col items-center gap-2 px-4 py-12 text-sm text-tertiary">
              <Inbox01 className="size-5 text-fg-quaternary" />
              {unreadOnly
                ? "You're all caught up."
                : "No notifications yet."}
            </div>
          )}

          {notifications.length > 0 && (
            <ul className="divide-y divide-secondary">
              {notifications.map((notification) => (
                <NotificationGridRow
                  key={notification.id}
                  notification={notification}
                  now={now}
                  isSelected={selectedIds.has(notification.id)}
                  onToggleSelected={() => toggleSelected(notification.id)}
                  onOpen={openNotification}
                  onDelete={setPendingDelete}
                />
              ))}
            </ul>
          )}

          {hasMore && (
            <div
              ref={sentinel}
              className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-tertiary"
            >
              {isLoadingMore && <Loading02 className="size-4 animate-spin" />}
              Loading more…
            </div>
          )}
        </div>
      </PageContentContainer>

      <DestructiveConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title={deleteCount === 1 ? "Delete notification" : "Delete notifications"}
        entity={
          pendingDelete && pendingDelete !== "selection"
            ? { label: pendingDelete.title, description: pendingDelete.message }
            : {
                label: `${deleteCount} ${deleteCount === 1 ? "notification" : "notifications"}`,
              }
        }
        confirmText="Delete"
        isLoading={deleteState.isDeleting}
      />
    </AdminLayout>
  );
}